// frontend/src/utils/responseShaper.js
import shapingRules from './shaping/shapingRules';
import { debugLog } from './logging/debugLog';
import resolveResponseShapingPolicy from './shaping/resolveResponseShapingPolicy';
import enforceResponsePolicy from './policy/enforceResponsePolicy';

const HEAVY_EMOTIONAL_INTENTS = new Set(['loss', 'grief']);
const FOLLOWUP_ALLOWED_INTENTS = new Set([
  'recall_request',
  'memory_recall',
  'direction',
  'faith_question',
  'prayer_request',
  'loss',
  'grief',
]);

const THERAPY_STYLE_QUESTION_PATTERNS = [
  /how are you feeling/i,
  /how do you feel/i,
  /how have you been holding up/i,
  /what is it about .* that makes you/i,
  /want to talk more about/i,
  /want to share more/i,
];

const VALIDATION_PATTERNS = [
  /that sounds/i,
  /that is quite/i,
  /that's wonderful/i,
  /that sounds wonderful/i,
  /that's heartwarming/i,
  /that sounds heartwarming/i,
  /i'm glad to hear/i,
  /i can understand/i,
  /it's understandable/i,
  /it sounds like/i,
  /that's really good to hear/i,
  /that makes sense/i,
  /it's natural/i,
];

function renderTemplate(template, context = {}) {
  return template.replace(/{{(\w+)}}/g, (_, key) => context[key] || '');
}

function normalizeRole(value = '') {
  return String(value).toLowerCase().trim();
}

function normalizeText(value = '') {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeMessageText(value = '') {
  return String(value || '')
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

function countRoleMatches(role, relationships = []) {
  if (!role || !Array.isArray(relationships)) return 0;

  const normalized = normalizeRole(role);

  return relationships.filter((rel) => {
    const r = normalizeRole(rel.relationship || rel.relationship_type);
    return r === normalized;
  }).length;
}

function shouldApplyPluralRoleGuard(intent = '') {
  return ['recall_request', 'memory_recall', 'loss'].includes(intent);
}

function splitIntoSentences(value = '') {
  const normalized = normalizeText(value);
  if (!normalized) return [];

  const matches = normalized.match(/[^.!?]+[.!?]?/g);
  return (matches || []).map((part) => part.trim()).filter(Boolean);
}

function joinSentences(sentences = []) {
  return normalizeText(sentences.join(' '));
}

function isQuestionSentence(sentence = '') {
  return /\?\s*$/.test(String(sentence).trim());
}

const INTERROGATIVE_LEADS = new Set([
  'who',
  'what',
  'where',
  'when',
  'why',
  'how',
  'is',
  'are',
  'am',
  'was',
  'were',
  'do',
  'does',
  'did',
  'can',
  'could',
  'will',
  'would',
  'should',
  'has',
  'have',
  'had',
  'may',
  'might',
  'shall',
  'isnt',
  'arent',
  'wasnt',
  'werent',
  'dont',
  'doesnt',
  'didnt',
  'cant',
  'couldnt',
  'wont',
  'wouldnt',
  'shouldnt',
  'hasnt',
  'havent',
  'hadnt',
]);

function escapeRegExp(value = '') {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getGroundedAnswerNames(reinjectedContext = {}) {
  return [
    reinjectedContext?.memoryName,
    reinjectedContext?.lastReferencedEntity?.name,
  ]
    .map((value) => normalizeText(value))
    .filter(Boolean);
}

function sentenceContainsGroundedAnswer(sentence = '', reinjectedContext = {}) {
  const names = getGroundedAnswerNames(reinjectedContext);
  if (!names.length) return false;

  return names.some((name) => {
    const pattern = new RegExp(`\\b${escapeRegExp(name)}\\b`, 'i');
    return pattern.test(sentence);
  });
}

function startsAsInformationSeekingQuestion(sentence = '') {
  const normalized = normalizeMessageText(sentence)
    .replace(/^[\s"'([{]+/, '')
    .trim();

  const firstWord = normalized.split(/\s+/)[0] || '';
  return INTERROGATIVE_LEADS.has(firstWord.replace(/[^a-z]/g, ''));
}

function preserveGroundedAnswerClauses(reply = '', reinjectedContext = {}) {
  const sentences = splitIntoSentences(reply);
  if (!sentences.length) return normalizeText(reply);

  const next = sentences.map((sentence) => {
    if (!isQuestionSentence(sentence)) return sentence;
    if (normalizeText(sentence).split(/\s+/).length < 2) return sentence;
    if (!sentenceContainsGroundedAnswer(sentence, reinjectedContext)) return sentence;
    if (startsAsInformationSeekingQuestion(sentence)) return sentence;

    // A model can express a grounded answer as a declarative clause followed
    // by a confirmation tag, e.g. "Yes, that's Ethan, right?". Runtime
    // question authority governs whether the model may ask a new question; it
    // must not erase a grounded answer merely because the sentence ends in a
    // question mark. Preserve the answer clause as a declaration and allow the
    // response-policy boundary to remove any genuinely optional question.
    const lastComma = sentence.lastIndexOf(',');

    if (lastComma > 0) {
      const answerClause = normalizeText(sentence.slice(0, lastComma));

      if (
        answerClause &&
        sentenceContainsGroundedAnswer(answerClause, reinjectedContext) &&
        !startsAsInformationSeekingQuestion(answerClause)
      ) {
        return `${answerClause.replace(/[.!?]+$/, '')}.`;
      }
    }

    return `${sentence.replace(/\?\s*$/, '').replace(/[.!]+$/, '')}.`;
  });

  return joinSentences(next);
}

function isValidationSentence(sentence = '') {
  const lower = normalizeText(sentence);
  return VALIDATION_PATTERNS.some((pattern) => pattern.test(lower));
}

function collapseValidationSentences(reply = '') {
  const sentences = splitIntoSentences(reply);

  if (sentences.length <= 1) {
    return normalizeText(reply);
  }

  let validationSeen = false;
  const next = [];

  for (const sentence of sentences) {
    const isValidation = isValidationSentence(sentence);

    if (isValidation) {
      if (validationSeen) {
        continue;
      }

      validationSeen = true;
    }

    next.push(sentence);
  }

  return joinSentences(next);
}

function shouldAllowFollowupQuestion(context = {}, tier = 'mid') {
  const policy =
    context?.shapingPolicy || resolveResponseShapingPolicy(context);

  if (policy.allowOptionalQuestion === true) return true;
  if (policy.allowOptionalQuestion === false) return false;

  const intent = context?.intent || '';
  const emotionLevel = Number(context?.emotionLevel || 0);

  if (FOLLOWUP_ALLOWED_INTENTS.has(intent)) {
    return true;
  }

  if (HEAVY_EMOTIONAL_INTENTS.has(intent)) {
    return true;
  }

  if (tier === 'full') {
    return true;
  }

  if (emotionLevel >= 0.8) {
    return true;
  }

  return false;
}

function removeUnauthorizedQuestionSentences(
  reply = '',
  context = {},
  tier = 'mid'
) {
  if (shouldAllowFollowupQuestion(context, tier)) {
    return normalizeText(reply);
  }

  const sentences = splitIntoSentences(reply);

  if (!sentences.length) {
    return normalizeText(reply);
  }

  const declarativeSentences = sentences.filter(
    (sentence) => !isQuestionSentence(sentence)
  );

  // Question authority is determined by runtime policy, not by where a
  // question appears in the reply. When optional questions are forbidden,
  // remove every interrogative sentence as long as the reply still contains
  // substantive declarative content. This covers leading, embedded, and
  // trailing questions without phrase-specific behavior rules.
  if (declarativeSentences.length) {
    return joinSentences(declarativeSentences);
  }

  // Do not erase an all-question reply here. A required clarification may have
  // been generated despite incomplete upstream metadata; preserving it is safer
  // than returning an empty response. Pending authority remains governed
  // separately by questionAuthority.
  return normalizeText(reply);
}

function stripTherapyStyleQuestions(reply = '', context = {}, tier = 'mid') {
  if (shouldAllowFollowupQuestion(context, tier)) {
    return normalizeText(reply);
  }

  const sentences = splitIntoSentences(reply);

  if (sentences.length <= 1) {
    return normalizeText(reply);
  }

  const filtered = sentences.filter((sentence, index) => {
    if (!isQuestionSentence(sentence)) return true;
    if (index === 0 && sentences.length === 1) return true;

    return !THERAPY_STYLE_QUESTION_PATTERNS.some((pattern) =>
      pattern.test(sentence)
    );
  });

  return joinSentences(filtered.length ? filtered : sentences);
}

function applyPronounGroundingFix(reply = '', reinjectedContext = {}) {
  const trimmedReply = normalizeText(reply);

  if (
    reinjectedContext?.lastReferencedEntity &&
    typeof trimmedReply === 'string'
  ) {
    const name = reinjectedContext.lastReferencedEntity.name;

    const vaguePatterns = [
      /who ["']?them["']? might be/i,
      /who they are/i,
      /who you're referring to/i,
    ];

    const isVague = vaguePatterns.some((pattern) =>
      pattern.test(trimmedReply)
    );

    if (isVague && name) {
      return trimmedReply.replace(
        /who ["']?them["']? might be|who they are|who you're referring to/i,
        `you're talking about ${name}`
      );
    }
  }

  return trimmedReply;
}

function buildTraceStep({
  id,
  label,
  input = '',
  output = '',
  reason = '',
  authority = null,
  decision = null,
  violations = [],
}) {
  const normalizedInput = normalizeText(input);
  const normalizedOutput = normalizeText(output);

  return {
    id,
    label,
    input: normalizedInput,
    output: normalizedOutput,
    changed: normalizedInput !== normalizedOutput,
    reason,
    authority,
    decision,
    violations: Array.isArray(violations) ? violations : [],
  };
}

function finalizeReply(
  reply = '',
  context = {},
  tier = 'mid',
  reinjectedContext = {}
) {
  const trace = [];
  let next = normalizeText(reply);

  const validationInput = next;
  next = collapseValidationSentences(next);

  trace.push(
    buildTraceStep({
      id: 'collapse_validation',
      label: 'Validation Collapse',
      input: validationInput,
      output: next,
      reason:
        'Removes repeated validation language while preserving the first meaningful validation sentence.',
    })
  );

  const questionInput = next;
  next = stripTherapyStyleQuestions(next, context, tier);

  trace.push(
    buildTraceStep({
      id: 'therapy_question_guard',
      label: 'Question Guard',
      input: questionInput,
      output: next,
      reason: shouldAllowFollowupQuestion(context, tier)
        ? 'Optional follow-up authority is available for this response mode.'
        : 'Optional follow-up authority is not available; unauthorized therapy-style questions are removed.',
      authority: context?.shapingPolicy?.questionAuthority || null,
    })
  );

  const pronounInput = next;
  next = applyPronounGroundingFix(next, reinjectedContext);

  trace.push(
    buildTraceStep({
      id: 'pronoun_grounding',
      label: 'Pronoun Grounding',
      input: pronounInput,
      output: next,
      reason:
        'Grounds vague references to the active entity when deterministic continuity is available.',
    })
  );

  const groundedAnswerInput = next;
  next = preserveGroundedAnswerClauses(next, reinjectedContext);

  trace.push(
    buildTraceStep({
      id: 'grounded_answer_preservation',
      label: 'Grounded Answer Preservation',
      input: groundedAnswerInput,
      output: next,
      reason:
        'Preserves a resolved answer expressed with question punctuation as declarative content before optional-question enforcement.',
      authority: context?.shapingPolicy?.questionAuthority || null,
    })
  );

  // Final response execution boundary.
  const policyInput = next;
  const governed = enforceResponsePolicy(policyInput, context, {
    stage: 'response_shaper',
  });

  const policyOutput = normalizeText(governed.response);

  trace.push(
    buildTraceStep({
      id: 'response_shaper_policy',
      label: 'Response Shaper Policy',
      input: policyInput,
      output: policyOutput,
      reason:
        'Validates the locally shaped candidate against the effective response policy contract.',
      authority:
        governed?.effectivePolicy?.authority ||
        governed?.validation?.policyAuthority ||
        null,
      decision: governed?.validation?.decision || null,
      violations: governed?.validation?.violations || [],
    })
  );

  return {
    response: policyOutput,
    trace,
    policyResult: {
      ...governed,
      stage: 'response_shaper',
      inputResponse: policyInput,
      outputResponse: policyOutput,
    },
  };
}

function buildReinjectedContext(context = {}) {
  const {
    activeIdentity = null,
    knownRelationships = [],
    memoryName = null,
    lastReferencedEntity = null,
    frame = null,
    intent = null,
    target = null,
    userName = 'friend',
    emotionLevel = 0,
    turnSignalArbitration = null,
    intentState = null,
    objectiveDecisionState = null,
    pendingQuestionState = null,
    admissibilityState = null,
    turnInterpretation = null,
    responseIntent = null,
    shapingPolicy = null,
  } = context;

  let resolvedMemoryName = memoryName;

  const roleMatches = countRoleMatches(target, knownRelationships);

  if (shouldApplyPluralRoleGuard(intent) && roleMatches > 1) {
    resolvedMemoryName = null;
  }

  return {
    activeIdentity,
    knownRelationships,
    memoryName: resolvedMemoryName,
    lastReferencedEntity,
    frame,
    intent,
    target,
    userName,
    emotionLevel,
    turnSignalArbitration,
    intentState,
    objectiveDecisionState,
    pendingQuestionState,
    admissibilityState,
    turnInterpretation,
    responseIntent,
    shapingPolicy:
      shapingPolicy || resolveResponseShapingPolicy(context),
  };
}

function getTemplateContext(reinjectedContext = {}) {
  const {
    userName = 'friend',
    target = '',
    memoryName = '',
    lastReferencedEntity = null,
  } = reinjectedContext;

  return {
    userName,
    target,
    memoryName,
    lastReferencedEntityName: lastReferencedEntity?.name || '',
    lastReferencedEntityRole: lastReferencedEntity?.role || '',
    lastReferencedEntityType: lastReferencedEntity?.type || '',
  };
}

function isWeakReply(trimmedReply, tier) {
  const isEmpty = !trimmedReply || trimmedReply.length === 0;
  const isWeakMid = tier === 'mid' && trimmedReply.length < 100;
  const isWeakFull = tier === 'full' && trimmedReply.length < 120;

  return isEmpty || isWeakMid || isWeakFull;
}

function isGreetingMessage(message = '') {
  const normalized = normalizeMessageText(message);

  if (!normalized) return false;

  return [
    'hi',
    'hello',
    'hey',
    'hi daisy',
    'hello daisy',
    'hey daisy',
    'hello again',
    'hi again',
    'hey again',
  ].includes(normalized);
}

function isRememberMeMessage(message = '') {
  const normalized = normalizeMessageText(message);

  return normalized === 'do you remember me' || normalized === 'remember me';
}

function isNameRecallMessage(message = '') {
  const normalized = normalizeMessageText(message);

  return [
    'do you remember my name',
    'remember my name',
    'what is my name',
    "what's my name",
    'who am i',
  ].includes(normalized);
}

function getResolvedFirstName(reinjectedContext = {}) {
  const activeIdentity = reinjectedContext?.activeIdentity || {};
  const displayName = normalizeText(activeIdentity?.displayName || '');
  const userName = normalizeText(reinjectedContext?.userName || '');

  if (displayName) {
    return displayName.split(/\s+/)[0];
  }

  if (userName && userName.toLowerCase() !== 'friend') {
    return userName.split(/\s+/)[0];
  }

  return '';
}

function isPluralReferenceRecallMessage(message = '') {
  const normalized = normalizeMessageText(message);

  return [
    'do you remember them',
    'remember them',
    'do you remember their names',
    'remember their names',
    'what are their names',
    "what're their names",
    "what's their names",
  ].includes(normalized);
}

function isSingularReferenceRecallMessage(message = '') {
  const normalized = normalizeMessageText(message);

  return [
    'do you remember her',
    'remember her',
    'do you remember him',
    'remember him',
    'do you remember her name',
    'remember her name',
    'what is her name',
    "what's her name",
    'do you remember his name',
    'remember his name',
    'what is his name',
    "what's his name",
  ].includes(normalized);
}

function getRelationshipDisplayName(rel = {}) {
  const firstName = normalizeText(rel.first_name || '');
  const lastName = normalizeText(rel.last_name || '');

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  return firstName || lastName || '';
}

function getPluralReferencedRelationships(reinjectedContext = {}) {
  const relationships = Array.isArray(
    reinjectedContext?.knownRelationships
  )
    ? reinjectedContext.knownRelationships
    : [];

  const target = normalizeRole(reinjectedContext?.target || '');

  if (!target) return [];

  return relationships.filter((rel) => {
    const role = normalizeRole(
      rel.relationship || rel.relationship_type
    );

    return role === target;
  });
}

function getDeterministicOverride(context = {}, reinjectedContext = {}) {
  const message = context?.message || '';
  const intent = context?.intent || '';
  const firstName = getResolvedFirstName(reinjectedContext);

  if (isGreetingMessage(message) || intent === 'greeting') {
    if (firstName) {
      return `Hey ${firstName} — good to hear from you.`;
    }

    return 'Hey — good to hear from you.';
  }

  if (isRememberMeMessage(message)) {
    if (firstName) {
      return `Yes — you're ${firstName}.`;
    }

    return 'Yes — I remember you.';
  }

  if (isNameRecallMessage(message)) {
    if (firstName) {
      return `Yes — your name is ${firstName}.`;
    }

    return "I don't have your name in front of me right now.";
  }

  if (isPluralReferenceRecallMessage(message)) {
    const matches =
      getPluralReferencedRelationships(reinjectedContext);

    if (matches.length > 1) {
      const names = matches
        .map((rel) => getRelationshipDisplayName(rel))
        .filter(Boolean);

      if (names.length > 1) {
        return `Yes — ${names.join(' and ')}.`;
      }

      if (names.length === 1) {
        return `Yes — ${names[0]}.`;
      }
    }

    if (matches.length === 1) {
      const name = getRelationshipDisplayName(matches[0]);

      if (name) {
        return `Yes — ${name}.`;
      }
    }
  }

  if (isSingularReferenceRecallMessage(message)) {
    const referencedName = normalizeText(
      reinjectedContext?.lastReferencedEntity?.name || ''
    );

    if (referencedName) {
      return `Yes — ${referencedName}.`;
    }
  }

  return null;
}

function shapeResponse(rawReply, context = {}) {
  const trimmedReply = normalizeText(rawReply);
  const reinjectedContext = buildReinjectedContext(context);

  debugLog('🔧 [Shaper] Raw reply:', trimmedReply);
  debugLog('🔧 [Shaper] Context:', context);
  debugLog(
    '🧩 [Shaper] Reinjected context:',
    reinjectedContext
  );

  const deterministicOverride = getDeterministicOverride(
    context,
    reinjectedContext
  );

  if (deterministicOverride) {
    debugLog(
      '🛡️ [Shaper Override] Deterministic override applied:',
      deterministicOverride
    );

    return finalizeReply(
      deterministicOverride,
      reinjectedContext,
      'short',
      reinjectedContext
    );
  }

  const {
    intent,
    memoryName,
    turnSignalArbitration,
    shapingPolicy,
  } = reinjectedContext;

  const effectiveIntent =
    turnSignalArbitration?.dominantTurnMode ===
    'contextual_continuation'
      ? 'contextual_continuation'
      : intent;

  const rule = shapingRules[effectiveIntent];
  const tier = rule?.shapingTier || 'mid';
  const templateContext =
    getTemplateContext(reinjectedContext);

  debugLog(`📐 [Shaper] Tier selected: ${tier}`);
  debugLog('🧭 [Shaper] Response policy:', shapingPolicy);

  const promptInstruction = rule?.promptInstruction;

  if (promptInstruction) {
    debugLog(
      `🧠 [PromptInjection] Using tier: ${tier} →`,
      promptInstruction
    );

    reinjectedContext.promptInstruction =
      promptInstruction;
  }

  debugLog(
    '🔧 [Shaper] Reinjected context after prompt injection:',
    reinjectedContext
  );

  if (tier === 'short') {
    if (rule?.variants?.length) {
      const variant =
        rule.variants[
          Math.floor(Math.random() * rule.variants.length)
        ];

      return finalizeReply(
        renderTemplate(variant, templateContext),
        reinjectedContext,
        tier,
        reinjectedContext
      );
    }
  }

  if (tier === 'mid') {
    if (rule?.variants?.length && trimmedReply.length < 100) {
      debugLog(
        '🔁 [Shaper] Mid-tier fallback: LLM too short → using variant'
      );

      const variant =
        rule.variants[
          Math.floor(Math.random() * rule.variants.length)
        ];

      return finalizeReply(
        renderTemplate(variant, templateContext),
        reinjectedContext,
        tier,
        reinjectedContext
      );
    }
  }

  if (rule?.useLocal) {
    if (
      effectiveIntent === 'recall_request' ||
      effectiveIntent === 'memory_recall'
    ) {
      const variantList =
        memoryName && rule.specificVariants
          ? rule.specificVariants
          : rule.generalVariants || rule.variants;

      if (variantList?.length) {
        const variant =
          variantList[
            Math.floor(Math.random() * variantList.length)
          ];

        return finalizeReply(
          renderTemplate(variant, templateContext),
          reinjectedContext,
          tier,
          reinjectedContext
        );
      }
    } else if (rule?.variants?.length) {
      const variant =
        rule.variants[
          Math.floor(Math.random() * rule.variants.length)
        ];

      return finalizeReply(
        renderTemplate(variant, templateContext),
        reinjectedContext,
        tier,
        reinjectedContext
      );
    }
  }

  if (
    isWeakReply(trimmedReply, tier) &&
    rule?.variants?.length
  ) {
    debugLog(
      '🔁 [Shaper Retry] Weak LLM response — falling back to variant'
    );

    const variant =
      rule.variants[
        Math.floor(Math.random() * rule.variants.length)
      ];

    return finalizeReply(
      renderTemplate(variant, templateContext),
      reinjectedContext,
      tier,
      reinjectedContext
    );
  }

  return finalizeReply(
    trimmedReply,
    reinjectedContext,
    tier,
    reinjectedContext
  );
}

export { shapeResponse, buildReinjectedContext };